import type { AnalysisRange } from "./analysis";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, App, Card, Empty, Space, Table, Tag, Tooltip, Typography, theme } from "antd";
import { Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, YAxis } from "recharts";
import {
  FIELD_STATUS_LABELS,
  type FieldQualityCell,
  type FieldQualityResponse,
  type FieldQualitySource,
  type FieldStatus,
} from "../../types/fieldQuality";

const { Paragraph, Text } = Typography;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

// Lokalne kopie helperów auth — konwencja tego panelu (patrz `pages/funnel/list.tsx`
// i `scraperDiagnostics/list.tsx`); nie ma wspólnego modułu i celowo go tu nie wprowadzamy.
function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string): Promise<T> {
  const response = await fetch(url, { headers: adminHeaders() });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

/** Ikona statusu — kolor NIGDY nie niesie znaczenia sam: obok zawsze jest znak i liczba. */
const STATUS_ICON: Record<FieldStatus, string> = {
  ok: "",
  warn: "▲",
  crit: "▲",
  debt: "•",
  insufficient: "—",
};

function fmtPct(value: number | null): string {
  return value === null ? "—" : `${value}%`;
}

function fmtTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pl-PL", { timeZone: "Europe/Warsaw" });
}

/**
 * Sparkline dziennego wypełnienia. Pokazuje KSZTAŁT zmiany, a nie dwie liczby: schodek
 * = zmiana layoutu z konkretnego dnia, piła = źródło z natury niestabilne. Bez tego
 * operator nie odróżni jednego od drugiego.
 */
const FillSparkline = ({ cell, color }: { cell: FieldQualityCell; color: string }) => {
  // Dni bez próbki (`pct: null`) zostawiamy jako przerwę w linii — dorysowanie ich jako
  // zera kłamałoby, że pole było puste, podczas gdy po prostu nic nie wpadło.
  const points: { day: string; pct: number | null }[] = cell.daily.map((day) => ({
    day: day.day,
    pct: day.pct,
  }));
  // Okno analizy DOKLEJAMY jako ostatni punkt — bez niego sparkline pokazywałby sam baseline
  // (płaską linię) i nie byłoby na nim widać właśnie tego spadku, dla którego go rysujemy.
  points.push({ day: "teraz", pct: cell.current_pct });
  // Sama liczba punktów nic nie mówi — po doklejeniu okna seria ma ich zawsze ≥ 1.
  // Rysujemy tylko wtedy, gdy jest co narysować: seria z samych `null` dałaby pusty
  // prostokąt udający wykres.
  if (!points.some((point) => point.pct !== null)) return <Text type="secondary">—</Text>;
  return (
    <ResponsiveContainer width="100%" height={44}>
      <LineChart data={points} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        {/* Skala 0–100 na sztywno: bez tego sparkline auto-skalowałby i szum wyglądałby
            jak katastrofa, a katastrofa jak szum. */}
        <YAxis domain={[0, 100]} hide />
        {/* Typy zostawiamy recharts do wywnioskowania — taka jest konwencja `list.tsx`. */}
        <ChartTooltip
          formatter={(value) => [`${String(value)}%`, "wypełnienie"]}
          labelFormatter={(label) => String(label)}
          contentStyle={{ fontSize: 12 }}
        />
        <Line
          type="monotone"
          dataKey="pct"
          stroke={color}
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export const FieldQualitySection = ({
  range,
  source,
  refreshKey,
}: {
  range: AnalysisRange;
  source: string;
  refreshKey: string | null;
}) => {
  const { notification } = App.useApp();
  const { token } = theme.useToken();
  const [data, setData] = useState<FieldQualityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestId = useRef(0);
  const { since, until } = range;

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setLoadError(null);
    try {
      const query = new URLSearchParams({ since, until });
      const response = await adminFetch<FieldQualityResponse>(
        `${API_URL}/api/v1/admin/scraper/field-quality?${query}`,
      );
      if (id === requestId.current) setData(response);
    } catch (error) {
      if (id !== requestId.current) return;
      setData(null);
      setLoadError(error instanceof Error ? error.message : "Błąd pobierania danych");
      notification.error({
        message: "Nie udało się pobrać jakości pól",
        description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
      });
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [notification, since, until]);

  useEffect(() => {
    void load();
    const requests = requestId;
    return () => {
      requests.current++;
    };
  }, [load, refreshKey]);

  /**
   * Tło komórki: JEDEN odcień, jasny → ciemny wraz z odsetkiem (skala sekwencyjna dla
   * wielkości). Status jedzie osobnym kanałem (obwódka + ikona), bo mieszanie „ile" i
   * „czy alarm" w tym samym kolorze dałoby tęczę, w której nie widać ani jednego, ani drugiego.
   */
  const cellStyle = useCallback(
    (cell: FieldQualityCell): React.CSSProperties => {
      if (cell.status === "insufficient") {
        return { background: "transparent" };
      }
      if (cell.status === "debt") {
        // Dług celowo POZA skalą sekwencyjną — szary neutralny. „Puste od zawsze" nie jest
        // najniższym stopniem jakości, tylko inną kategorią, i nie może wyglądać jak alarm.
        return { background: token.colorFillSecondary };
      }
      const pct = cell.current_pct ?? 0;
      // Alfa zamiast gotowych stopni: to ten sam odcień (`colorPrimary`) na powierzchni karty,
      // więc działa i w jasnym, i w ciemnym motywie bez drugiej palety.
      const alpha = 0.08 + (pct / 100) * 0.82;
      const border =
        cell.status === "crit"
          ? `2px solid ${token.colorError}`
          : cell.status === "warn"
            ? `2px solid ${token.colorWarning}`
            : undefined;
      return { background: `rgba(31, 128, 129, ${alpha.toFixed(3)})`, border };
    },
    [token],
  );

  /** Kolor tekstu w komórce — ink, nigdy kolor serii; przy ciemnym tle jasny, żeby dało się przeczytać. */
  const cellTextColor = useCallback(
    (cell: FieldQualityCell): string | undefined => {
      if (cell.status === "insufficient" || cell.status === "debt") return token.colorTextSecondary;
      return (cell.current_pct ?? 0) > 55 ? "#FFFFFF" : token.colorText;
    },
    [token],
  );

  const columns = useMemo(() => {
    const fields = data?.fields ?? [];
    return [
      {
        title: "Źródło",
        dataIndex: "source_name",
        fixed: "left" as const,
        width: 180,
        render: (source: string, row: FieldQualitySource) => (
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 13 }}>
              {source}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.records} rek. w oknie
            </Text>
          </Space>
        ),
      },
      ...fields.map((field) => ({
        title: (
          <Tooltip
            title={
              field.maturity_sensitive
                ? `${field.label} — pole dopełnia się już po utworzeniu ogłoszenia (załączniki dociągane osobno), więc mierzymy je, ale nie alarmujemy: świeże ogłoszenia z natury mają go mniej.`
                : field.label
            }
          >
            <span style={{ fontSize: 11 }}>
              {field.label}
              {field.maturity_sensitive ? " *" : ""}
            </span>
          </Tooltip>
        ),
        dataIndex: field.key,
        align: "center" as const,
        width: 96,
        render: (_: unknown, row: FieldQualitySource) => {
          const cell = row.cells.find((c) => c.field === field.key);
          if (!cell) return <Text type="secondary">—</Text>;
          return (
            <Tooltip title={cell.reason}>
              <div
                style={{
                  ...cellStyle(cell),
                  borderRadius: 4,
                  padding: "6px 4px",
                  cursor: "help",
                  // 2px oddechu między wypełnieniami — sąsiadujące komórki nie mogą się zlewać
                  // w jedną plamę, bo wtedy nie widać granicy pola.
                  margin: 1,
                }}
              >
                <Text strong style={{ fontSize: 12, color: cellTextColor(cell) }}>
                  {STATUS_ICON[cell.status] && cell.status !== "insufficient"
                    ? `${STATUS_ICON[cell.status]} `
                    : ""}
                  {cell.status === "insufficient" ? "—" : fmtPct(cell.current_pct)}
                </Text>
              </div>
            </Tooltip>
          );
        },
      })),
    ];
  }, [data, cellStyle, cellTextColor]);

  const regressionColumns = useMemo(
    () => [
      { title: "Źródło", dataIndex: "source_name", width: 160 },
      { title: "Pole", dataIndex: "label", width: 150 },
      {
        title: "Ocena",
        dataIndex: "status",
        width: 110,
        render: (status: FieldStatus) => (
          <Tag color={status === "crit" ? token.colorError : token.colorWarning}>
            {STATUS_ICON[status]} {FIELD_STATUS_LABELS[status]}
          </Tag>
        ),
      },
      {
        title: "Teraz",
        dataIndex: "current_pct",
        width: 90,
        render: (pct: number | null, row: FieldQualityCell) => (
          <Text strong style={{ color: token.colorError }}>
            {fmtPct(pct)}{" "}
            <Text type="secondary" style={{ fontWeight: 400, fontSize: 11 }}>
              ({row.current_filled}/{row.current_total})
            </Text>
          </Text>
        ),
      },
      {
        title: "Baseline (mediana)",
        dataIndex: "baseline_pct",
        width: 140,
        render: (pct: number | null, row: FieldQualityCell) => (
          <Text>
            {fmtPct(pct)}{" "}
            <Text type="secondary" style={{ fontSize: 11 }}>
              z {row.baseline_days} dni
            </Text>
          </Text>
        ),
      },
      {
        title: "Spadek",
        dataIndex: "drop_pp",
        width: 110,
        render: (drop: number | null, row: FieldQualityCell) => (
          <Tooltip
            title={`Próg alarmu: ${row.threshold_pp} pp (max z 10 pp i 3 × MAD ${row.baseline_mad} pp)`}
          >
            <Text strong>{drop === null ? "—" : `${drop} pp`}</Text>
          </Tooltip>
        ),
      },
      {
        title: "Trend dzienny",
        dataIndex: "daily",
        width: 160,
        render: (_: unknown, row: FieldQualityCell) => (
          <FillSparkline cell={row} color={token.colorError} />
        ),
      },
    ],
    [token],
  );

  const filteredSources =
    data?.sources.filter((row) => !source || row.source_name === source) ?? [];
  const summary = { ok: 0, warn: 0, crit: 0, debt: 0, insufficient: 0 };
  for (const row of filteredSources) for (const cell of row.cells) summary[cell.status]++;
  const regressions =
    data?.regressions.filter((row) => !source || row.source_name === source) ?? [];

  return (
    <Card
      size="small"
      title="Jakość pól — czy źródła nadal zapisują to, co powinny"
      style={{ marginBottom: 24 }}
      loading={loading && !data}
    >
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        Liczniki runów są ŚLEPE na tę klasę awarii: źródło może zapisać komplet rekordów z pustym
        polem i nadal będzie to <Text code>created</Text>, czyli „zdrowo". Dlatego liczymy wprost z
        kolekcji <Text code>auctions</Text>, bez udziału telemetrii. Alarmem jest{" "}
        <Text strong>odchylenie od baseline'u DANEGO źródła</Text> (mediana z{" "}
        {data?.baseline.days ?? 14} dni), nie próg absolutny — bo pole puste od zawsze to{" "}
        <Text strong>dług</Text> (szare), a nie regresja, i świeciłoby czerwono na zawsze.
      </Paragraph>

      {loading && data && (
        <Alert
          type="info"
          message="Trwa odświeżanie jakości pól. Widoczne dane dotyczą okresu podanego pod tabelą."
        />
      )}
      {loadError && <Alert type="error" showIcon message="Jakość pól jest niedostępna" description={loadError} />}
      {data && !data.available && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Brak danych o jakości pól"
          description="Agregacja kolekcji `auctions` nie powiodła się. Puste komórki poniżej NIE znaczą „0% wypełnienia” — znaczą, że panel nie wie."
        />
      )}

      {data?.available && regressions.length > 0 && (
        <Alert
          type={regressions.some((r) => r.status === "crit") ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 16 }}
          message={`Wykryto ${regressions.length} odchyleń od baseline'u`}
          description="Pole nagle przestało się zapisywać w stosunku do własnej historii tego źródła — typowy objaw zmiany layoutu portalu."
        />
      )}

      {data?.available && summary && (
        <Space size={[8, 8]} wrap style={{ marginBottom: 16 }}>
          <Tag color={token.colorError}>▲ Regresje: {summary.crit}</Tag>
          <Tag color={token.colorWarning}>▲ Spadki: {summary.warn}</Tag>
          <Tag>OK: {summary.ok}</Tag>
          <Tag>• Dług (od zawsze puste): {summary.debt}</Tag>
          <Tag>— Brak sygnału: {summary.insufficient}</Tag>
        </Space>
      )}

      {data?.available && regressions.length > 0 && (
        <Table
          size="small"
          rowKey={(row) => `${row.source_name}-${row.field}`}
          dataSource={regressions}
          columns={regressionColumns}
          pagination={false}
          style={{ marginBottom: 24 }}
          scroll={{ x: 920 }}
        />
      )}

      {data?.available && data.sources.some((row) => !source || row.source_name === source) ? (
        <>
          <Table
            size="small"
            rowKey="source_name"
            dataSource={filteredSources}
            columns={columns}
            pagination={false}
            scroll={{ x: 1100 }}
          />
          <Paragraph type="secondary" style={{ marginTop: 12, fontSize: 11 }}>
            Kolor = odsetek wypełnienia (jaśniej → ciemniej). Czerwona obwódka ▲ = regresja względem
            baseline'u tego źródła. Szare • = <Text strong>dług</Text>: pole puste od zawsze (np.
            Platforma Zakupowa nie zapisuje miasta — <Text code>auctionRowMapper.ts</Text> ustawia
            tylko kraj); to zgłoszony problem scrapera, nie nowa awaria. „—" = za mała próbka albo
            pomiar obciążony (kolumny z <Text code>*</Text>) — panel nie orzeka, zamiast zgadywać.
            Baseline: mediana z {data.baseline.days} dni, próg alarmu = max(10 pp, 3 × MAD danego
            pola).
          </Paragraph>
          <Paragraph type="secondary" style={{ marginTop: 4, fontSize: 11 }}>
            Wygenerowano: {fmtTime(data.generated_at)} · okno: {fmtTime(data.window.since)} —{" "}
            {fmtTime(data.window.until)} · baseline: {fmtTime(data.baseline.since)} —{" "}
            {fmtTime(data.baseline.until)}
          </Paragraph>
        </>
      ) : (
        data?.available && <Empty description="Brak ogłoszeń w oknie analizy" />
      )}
    </Card>
  );
};
